import { useEffect, useRef, useState } from 'react'
import FileService from '@/modules/file/fileService'
import DocumentAnalysisService from '@/modules/document/services/documentAnalysis'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import { isReviewEntryStatus } from './invoice-review-entry'
import { isAnalysedNonInvoice } from '@/modules/document/utils/invoice-analysis-state'

interface SelectionOptions {
  useDocuAiEnabled: boolean
  isNewReceipt: boolean
  readOnly?: boolean
  onReview?: (fileId: string) => void
}

// The same host also owns the non-invoice dialog and its manual-entry path. A terminal
// non-invoice is a usable result here, never a reason to call the analysis endpoint again.
const canOpenReview = (file) => isReviewEntryStatus(file?.documentInvoiceDetails?.reviewStatus)
  || isAnalysedNonInvoice(file)
  || file?.metaData?.analysisInProgress === true
  || file?.metaData?.ocr?.isRunning === true

// FLAG:useDocuAi — explicit document-container selection on a NEW expense/revenue.
// Existing review data is reused; otherwise request one DMS/DocuAI run before mounting the
// polling review. A plain GET /analyze can merely return a cached terminal without starting work.
export const useSelectedReceiptReview = ({ useDocuAiEnabled, isNewReceipt, readOnly, onReview }: SelectionOptions) => {
  const enabled = useDocuAiEnabled && isNewReceipt && !readOnly && !!onReview
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const onReviewRef = useRef(onReview)
  onReviewRef.current = onReview
  const pending = useRef<{ id: string } | null>(null)
  const [isPreparing, setIsPreparing] = useState(false)
  useEffect(() => () => { pending.current = null }, [])

  const cancel = () => { pending.current = null; setIsPreparing(false) }
  const selectDocument = async(selectedFile: { id?: string | number }) => {
    if (!enabledRef.current || selectedFile?.id == null) { return }
    const id = String(selectedFile.id)
    if (pending.current?.id === id) { return }
    const selection = { id }
    pending.current = selection
    setIsPreparing(true)
    const isCurrent = () => pending.current === selection && enabledRef.current
    try {
      // The container payload can be stale or omit documentInvoiceDetails.
      const { body } = await FileService.fetchFileInfo(id)
      if (!isCurrent()) { return }
      let ready = canOpenReview(body)
      if (!ready) {
        // The shared service keeps quota/refusal/error messages and the DMS concurrency guard.
        ready = await DocumentAnalysisService.refreshAnalysis({ id })
        if (!isCurrent()) { return }
        if (!ready) {
          // Another caller may have started/completed it between our lookup and the request.
          const { body: latest } = await FileService.fetchFileInfo(id)
          if (!isCurrent()) { return }
          ready = canOpenReview(latest)
        }
      }
      if (ready) { onReviewRef.current?.(id) }
    } catch {
      if (isCurrent()) { toast.showToast({ type: 'error', message: lang.file.analyze.errorMessage }) }
    } finally {
      if (pending.current === selection) { pending.current = null; setIsPreparing(false) }
    }
  }

  // Undefined means the legacy container still only assigns and closes, including saved receipts.
  return { onSelectDocument: enabled ? selectDocument : undefined, isPreparing, cancel }
}
